import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import OdooHeader from "@components/LandingPageComponents/OdooHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import goireland from "public/goireland.png";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/zoho-books/card1.png",
        title: "Minimise Invoice Errors and Ensure Accuracy",
        description: "Tired of dealing with invoice errors that lead to payment delays? Connect Zoho Books to WhatsApp and share accurate invoices in just a click.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-books/card2.png",
        title: "Tackle Your Snail-Paced Payment Cycles Head-on ",
        description: "Pave the way for speedy payment cycles with our automated chatbots and drip marketing that help you stay on top of your collection goals.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-books/card3.png",
        title: "Automated Payment Alerts that Work Like a Charm",
        description: "Collect outstanding payments efficiently using automated reminders (1 week, 3 days, 24 hours, and more) that nudge your customers to pay promptly.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-books/card4.png",
        title: " Send Confirmation Messages Without Manual Effort",
        description: "Still sending confirmation messages to your customers manually? Set up triggers to send instant confirmation messages as soon as a payment is received.",
    },
];

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: " Can we integrate Zoho Books with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Using Gallabox, you can establish the Zoho Books API trigger to run a workflow that integrates with the WhatsApp Business API. Its WhatsApp Automation Platform makes it possible to integrate Zoho Books and WhatsApp Business with unmatched speed and accuracy.",
            },
        },
        {
            "@type": "Question",
            name: "How to integrate Zoho Books and WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "WhatsApp and Zoho Books can be connected using data integration platforms like Zapier or dedicated WhatsApp Automation Tools like Gallabox. While using tools like Zapier may demand significant effort and time, investing in an AI-powered Meta Tech Partner like Gallabox simplifies the process with various pre-built, native third-party integrations.",
            },
        },
        {
            "@type": "Question",
            name: "What are the benefits of integrating WhatsApp with Zoho Books?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "While a seamless connection between your WhatsApp Business account and Zoho Books can offer a ton of benefits, here are some indispensable reasons why you need to focus on getting that integration right: 1.Ensure accuracy in accounting,2.Make reconciliation hassle-free,3.Speed up payment collection ,4.Accelerate and stabilise cash flow,5.Eliminate manual inefficiencies ",
            },
        },
    ],
};

const zohobooks: React.FC = () => {
    return (
        <NewLayout
            title="Zoho Books WhatsApp Integration | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/zoho-books/og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/zoho-books"
            description="Automate invoices and reminders with Zoho Books WhatsApp integration by Gallabox. Manage payments and updates via WhatsApp."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <div className="bg-white">
                <OdooHeader
                    pageTitle="WhatsApp Zoho Books Integration"
                    mainTitle="Zoho Books WhatsApp Integration for Seamless Accounting"
                    description="Ensure seamless data exchange with your accounting system and WhatsApp to improve accuracy and reduce redundancy.  "
                    buttonText="Sign up for free"
                    imageUrl="https://assets.gallabox.com/gb-home/zoho-books/header.png"
                />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/zoho-books/sec1.png"
                    title="Share Estimates and Quotes from WhatsApp"
                    description={
                        <p className="lg:text-base text-base lg:text-left text-center text-gray-600 lg:font-normal">
                            With ease, create quotes and estimates from a deal, sales order, or template on{" "}
                            <a href="https://go.zoho.com/jIt" target="_blank" rel="noreferrer" className="text-blue-500">
                                Zoho Books.
                            </a>{" "}
                            Using Gallabox’s Zoho Books Integration, empower your employees to send timely quotations via WhatsApp.
                        </p>
                    }
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/zoho-books/sec2.png"
                    title="Send Automated Payment Reminders on Time"
                    description="Invoice and estimate reminders are no longer a manual process. Our intelligent chatbots can help you send automated payment reminders to your existing and potential customers, keeping your cash flow healthy."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/zoho-books/sec3.png"
                    title="Don’t Keep Your Customers Waiting for Confirmation"
                    description="Is your team facing a barrage of irate customer queries requesting updates about their payment status? Take the burden off your team’s shoulders by sending automated WhatsApp confirmation right after receiving a payment."
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/zoho-books/sec4.png"
                    title="One-Click Integration for Optimised Accounting"
                    description={
                        <p className="lg:text-base text-base lg:text-left text-center text-gray-600 lg:font-normal">
                            Who said integration cycles are long and riddled with complexities? Connect Gallabox with your{" "}
                            <a href="https://go.zoho.com/jIt" target="_blank" rel="noreferrer" className="text-blue-500">
                                Zoho Books
                            </a>{" "}
                            account in a few clicks to manage your book of accounts and reconcile sales, collections, and cash flow effectively.
                        </p>
                    }
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/zoho-books/sec5.png"
                    title="Speed Up Your Collection Cycle with Template Library"
                    description="Rather than writing something from the ground up, you can pick from our pre-approved, ready-to-use library of templates to reduce the response time and capitalise on a customer’s heightened interest during the initial stages."
                />

                <YellowCTAhref
                    buttonText="Get Started Today"
                    customText={
                        <h3 className="text-2xl md:text-3xl font-bold text-yellow-800">
                            Strike the Golden Accounting Deal by Automating <br /> Your WhatsApp Operations
                        </h3>
                    }
                />

                <TestimonialSection
                    TestimonialSectionPropsData={[
                        {
                            companyImg: goireland,
                            headerTitle: "Gallabox’s WhatsApp Widget reduced FTR  from 15-30 minutes to less than a minute, boosting engagement and conversion rates.",
                            avatarImg: "https://assets.gallabox.com/gb-home/go-ireland/goireland-owner.png",
                            nameText: "Ilaya Bharathi",
                            professionText: "Founder & CEO, GoIreland",
                        },
                    ]}
                />

                <ImageGrid />

                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about Zoho Books-Gallabox integration</h3>
                    </div>
                    <AccordionCustom
                        title="Zoho Books-Gallabox integration"
                        items={[
                            {
                                question: "Can we integrate Zoho Books with WhatsApp?",
                                answer: "Using Gallabox, you can establish the Zoho Books API trigger to run a workflow that integrates with the WhatsApp Business API. Its WhatsApp Automation Platform makes it possible to integrate Zoho Books and WhatsApp Business with unmatched speed and accuracy.",
                            },
                            {
                                question: "How to integrate Zoho Books and WhatsApp?",
                                answer: "WhatsApp and Zoho Books can be connected using data integration platforms like Zapier or dedicated WhatsApp Automation Tools like Gallabox. While using tools like Zapier may demand significant effort and time, investing in an AI-powered Meta Tech Partner like Gallabox simplifies the process with various pre-built, native third-party integrations.",
                            },
                            {
                                question: "What are the benefits of integrating WhatsApp with Zoho Books?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "While a seamless connection between your WhatsApp Business account and Zoho Books can offer a ton of benefits, here are some indispensable reasons why you need to focus on getting that integration right: "
                                            }
                                        </span>

                                        <ol className="pl-1">
                                            <li>{"Ensure accuracy in accounting"}</li>
                                            <li>{"Make reconciliation hassle-free"}</li>
                                            <li>{"Speed up payment collection "}</li>
                                            <li>{"Accelerate and stabilise cash flow"}</li>
                                            <li>{"Eliminate manual inefficiencies "}</li>
                                        </ol>
                                    </>
                                ),
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default zohobooks;
